import React, { useEffect, useState, useRef } from 'react';
// For microphone/voice search
// @ts-ignore
const SpeechRecognition: typeof window.SpeechRecognition | undefined = window.SpeechRecognition || window.webkitSpeechRecognition;


type BibleBook = { name: string; chapters: { name: string }[] };
type BibleStructure = { books: BibleBook[] };

type HighlightColor = 'yellow' | 'green' | 'blue' | 'pink' | '';

const BibleExplorer: React.FC<{onInfo?:()=>void, defaultVersion?: string, accessibilityMode?: boolean}> = ({onInfo, defaultVersion = 'KJV', accessibilityMode = false}) => {
    // ...existing code...

    // ...existing useState and useEffect hooks...

    // ...existing useState and useEffect hooks...

    // ...existing useState and useEffect hooks...
  // Donate modal state
  const [showDonate, setShowDonate] = useState(false);
  // State
  const [lines, setLines] = useState<string[]>([]);
  // (Removed duplicate state declarations below)
  const [highlighted, setHighlighted] = useState<{[key: number]: HighlightColor}>({});
  const [annotations, setAnnotations] = useState<{[key: number]: string}>({});
  // (Dark mode removed for now)
  const [annotateLine, setAnnotateLine] = useState<number|null>(null);
  const [highlightDialog, setHighlightDialog] = useState<{open: boolean, line: number|null}>({open: false, line: null});
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const [search, setSearch] = useState<string>('');
  const [showAll, setShowAll] = useState<boolean>(true);
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState('Genesis');
  const [selectedChapter, setSelectedChapter] = useState('1');
  const [bibleStructure, setBibleStructure] = useState<BibleStructure|null>(null);
  const [selected, setSelected] = useState('');
  const [versions, setVersions] = useState<{name: string, file: string}[]>([]);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);

  // Styles removed: all styling now handled by CSS classes
  const highlightColors: HighlightColor[] = ['', 'yellow', 'green', 'blue', 'pink'];

  // Load all available Bible versions from bibles.json manifest
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/bibles/bibles.json')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load Bible manifest');
        return res.json();
      })
      .then((files: {name: string, file: string}[]) => {
        setVersions(files);
        // Set default based on prop or KJV
        const versionToUse = defaultVersion.endsWith('.json') ? defaultVersion : `${defaultVersion}.json`;
        const found = files.find(v => v.file === versionToUse || v.file.toLowerCase() === `${defaultVersion.toLowerCase()}.json`);
        if (found) {
          setSelected(found.file);
        } else {
          // Fallback to KJV if not found
          const kjv = files.find(v => v.file.toLowerCase() === 'kjv.json');
          if (kjv) setSelected(kjv.file);
        }
      })
      .catch(err => {
        alert('Error loading Bible manifest: ' + err.message);
      });
  }, [defaultVersion]);

  // Load Bible text and structure
  useEffect(() => {
    if (!selected) return;
    fetch(process.env.PUBLIC_URL + '/bibles/' + selected)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load Bible file: ' + selected);
        return res.json();
      })
      .then(data => {
        const flatLines: string[] = [];
        if (data.books) {
          data.books.forEach((book: any) => {
            flatLines.push(`# ${book.name}`);
            book.chapters.forEach((chapter: any) => {
              flatLines.push(`## ${chapter.name || chapter.chapter}`);
              chapter.verses.forEach((verse: any) => {
                flatLines.push(`${verse.name} ${verse.text}`);
              });
            });
          });
        }
        setLines(flatLines);
        setBibleStructure({ books: data.books.map((b: any) => ({
          name: b.name,
          chapters: b.chapters.map((c: any) => ({ name: c.name || c.chapter }))
        })) });
        setSelectedBook(data.books[0]?.name || 'Genesis');
        setSelectedChapter(data.books[0]?.chapters[0]?.name || '1');
      })
      .catch(err => {
        alert('Error loading Bible file: ' + err.message);
      });
  }, [selected]);

  // Load highlights/annotations from localStorage
  useEffect(() => {
    if (!selected) return;
    // Always load highlights and annotations from localStorage
    try {
      const h = localStorage.getItem('highlights_' + selected);
      setHighlighted(h ? JSON.parse(h) : {});
    } catch { setHighlighted({}); }
    try {
      const a = localStorage.getItem('annotations_' + selected);
      setAnnotations(a ? JSON.parse(a) : {});
    } catch { setAnnotations({}); }
  }, [selected]);

  // Reset search state when lines change (e.g., version switch)
  useEffect(() => {
    setShowAll(true);
    setSearch('');
    setSearchResults([]);
  }, [lines]);

  // Fuzzy search with Fuse.js, only on Enter
  const fuseRef = useRef<any>(null);
  useEffect(() => {
    // Only verse lines
    const verseLines = lines.map((line, i) => ({ line, i })).filter(obj => !obj.line.startsWith('#') && !obj.line.startsWith('##'));
    // @ts-ignore
    fuseRef.current = window.Fuse ? new window.Fuse(verseLines, { keys: ['line'], threshold: 0.36 }) : null;
  }, [lines]);

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      if (!search.trim() || !fuseRef.current) {
        setShowAll(true);
        setSearchResults([]);
        return;
      }
      setTimeout(() => {
        const results = fuseRef.current.search(search.trim()).map((r: any) => r.item.i);
        setShowAll(false);
        setSearchResults(results);
      }, 0);
    }
  }

  // Menu feature handlers
  function handleBookmark() { alert('Bookmarks feature coming soon!'); }
  function handleExportImport() { alert('Export/Import feature coming soon!'); }
  function handleCompare() { alert('Verse comparison feature coming soon!'); }
  function handleAccessibility() { alert('Accessibility improvements coming soon!'); }

  // Utility for highlight color
  function getHighlightColor(color: HighlightColor) {
    switch (color) {
      case 'yellow': return '#da70d6';
      case 'green': return '#d4f7d4';
      case 'blue': return '#d4e6fa';
      case 'pink': return '#fbe0f3';
      default: return '';
    }
  }

  // Random scripture feature
  function getRandomScripture(): void {
    if (!lines.length) return;
    let verseLines = lines.map((line: string, i: number) => ({line, i})).filter((l: {line: string, i: number}) => l.line && !l.line.startsWith('#') && !l.line.startsWith('##'));
    if (verseLines.length === 0) return;
    let random = verseLines[Math.floor(Math.random() * verseLines.length)];
    alert(`🎲 Random Scripture:\n${random.line}`);
  }

  // Cycle highlight color
  const handleHighlight = (line: number): void => {
    setHighlightDialog({open: true, line});
  };

  // Confirm highlight
  const confirmHighlight = (color: HighlightColor): void => {
    if (highlightDialog.line !== null) {
      setHighlighted((h: {[key: number]: HighlightColor}) => {
        const updated = { ...h };
        if (color) {
          updated[highlightDialog.line!] = color;
        } else {
          delete updated[highlightDialog.line!];
        }
        if (selected) localStorage.setItem('highlights_' + selected, JSON.stringify(updated));
        return updated;
      });
    }
    setHighlightDialog({open: false, line: null});
  };

  // Voice search logic
  const transcriptBuffer = useRef('');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceHighlightIdx, setVoiceHighlightIdx] = useState<number|null>(null);
  const [voiceError, setVoiceError] = useState<string|null>(null);

  const startListening = () => {
    if (!SpeechRecognition) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
    }
    recognitionRef.current.onresult = (event: any) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript;
        else interim += event.results[i][0].transcript;
      }
      transcriptBuffer.current += final + interim;
      let transcript = transcriptBuffer.current.trim();
      // Clean up repeated words (e.g., 'John John 3 John 3 16')
      transcript = transcript.replace(/(\b\w+\b)(?: \1)+/gi, '$1');
      setVoiceTranscript(transcript);
      // Regex for Book Chapter:Verse
      const verseRegex = /([1-3]?\s?[A-Za-z]+)\s*(?:chapter)?\s*(\d+)[\s:,-]*(?:verse)?\s*(\d+)/i;
      // Regex for Book Chapter only
      const chapterRegex = /([1-3]?\s?[A-Za-z]+)\s*(?:chapter)?\s*(\d+)/i;
      // Make navigation more forgiving: allow "Ecclesiastes five" or "John three sixteen"
      let verseMatch = transcript.match(verseRegex);
      if (!verseMatch) {
        // Try forgiving: "Book Chapter Verse" (no colon)
        const loose = transcript.match(/([1-3]?\s?[A-Za-z]+)\s+(\d+)\s+(\d+)/i);
        if (loose) verseMatch = loose;
      }
      if (verseMatch) {
        let book = verseMatch[1].replace(/\s+/g, ' ').trim();
        let chapter = verseMatch[2];
        let verse = verseMatch[3];
        // Try several label formats for robustness
        const candidates = [
          `${book} ${chapter}:${verse}`,
          `${book} ${chapter} ${verse}`,
          `${book} ${chapter}.${verse}`
        ];
        let idx = -1;
        for (const label of candidates) {
          // Only match verse lines (not headings)
          idx = lines.findIndex((line: string, i: number) => !line.startsWith('#') && line.toLowerCase().startsWith(label.toLowerCase()));
          if (idx !== -1) break;
        }
        if (idx !== -1) {
          setVoiceHighlightIdx(idx);
          setTimeout(() => setVoiceHighlightIdx(null), 3000);
          setTimeout(() => {
            const el = document.querySelector(`[data-verse-idx="${idx}"]`);
            if (el) (el as HTMLElement).scrollIntoView({behavior:'smooth', block:'center'});
          }, 50); // Wait for highlight to render
          transcriptBuffer.current = '';
          setVoiceTranscript('');
        } else {
          setVoiceError(`Verse not found: ${book} ${chapter}:${verse} (Heard: '${transcript}')`);
          transcriptBuffer.current = '';
          setVoiceTranscript('');
        }
      } else {
        // Try Book Chapter navigation if only chapter is spoken
        const chapterMatch = transcript.match(chapterRegex);
        if (chapterMatch) {
          let book = chapterMatch[1].replace(/\s+/g, ' ').trim();
          let chapter = chapterMatch[2];
          // Find chapter heading (e.g., '## John 3')
          const idx = lines.findIndex((line: string) => line.startsWith('## ') && line.replace('## ', '').toLowerCase() === `${book} ${chapter}`.toLowerCase());
          if (idx !== -1) {
            const el = document.querySelector(`[data-verse-idx="${idx}"]`);
            if (el) (el as HTMLElement).scrollIntoView({behavior:'smooth', block:'center'});
            transcriptBuffer.current = '';
            setVoiceTranscript('');
          } else {
            setVoiceError(`Chapter not found: ${book} ${chapter} (Heard: '${transcript}')`);
            transcriptBuffer.current = '';
            setVoiceTranscript('');
          }
        }
      }
    };
    recognitionRef.current.onerror = (event: any) => {
      alert('Speech recognition error: ' + event.error);
      console.error('[Voice] SpeechRecognition error:', event.error);
      setListening(false);
      transcriptBuffer.current = '';
      setVoiceTranscript('');
    };
    recognitionRef.current.onend = () => {
      // Only stop listening if user pressed stop
      if (listening) recognitionRef.current.start();
    };
    transcriptBuffer.current = '';
    setVoiceTranscript('');
    setListening(true);
    recognitionRef.current.start();
  };

  const stopListening = (): void => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  // Annotation logic
  const handleAnnotate = (line: number): void => {
    setAnnotateLine(line);
  };
  const saveAnnotation = (line: number, text: string): void => {
    setAnnotations((a: {[key: number]: string}) => {
      const updated = { ...a, [line]: text };
      if (selected) localStorage.setItem('annotations_' + selected, JSON.stringify(updated));
      return updated;
    });
    setAnnotateLine(null);
  };

  // Cancel highlight dialog
  const cancelHighlight = () => {
    setHighlightDialog({ open: false, line: null });
  };

  // Focus trap for menu (optional, for accessibility)
  useEffect(() => {
    if (menuOpen) {
      const firstBtn = document.querySelector('.main-menu button');
      if (firstBtn) (firstBtn as HTMLElement).focus();
    }
  }, [menuOpen]);


  return (
    <div>
      {/* Donate Button next to Verlume text in header */}
      {/* Unified Illuminated Header with Actual Logo and Donate Button */}
      <nav className="illuminated-header" style={{display:'flex',alignItems:'center',padding:'1.1rem 1rem 0.7rem 1rem',background:'var(--bg-secondary)',gap:'0.7rem', flexWrap: 'wrap', minHeight: '60px'}}>
        <button 
          onClick={() => setMenuOpen(!menuOpen)} 
          aria-label="Toggle navigation menu"
          style={{background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'none',borderRadius:6,padding:'0.4rem 0.7rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',minWidth:'44px',minHeight:'44px',transition:'all 0.2s'}}
        >
          <span className="material-icons">menu</span>
        </button>
        <span className="illuminated-logo" style={{display:'flex',alignItems:'center'}}>
          <img src="/verlume-logo.png" alt="Verlume Logo" style={{width: 36, height: 36, borderRadius: '50%', boxShadow:'0 0 0 2px #9D4EDD, 0 0 12px #9D4EDD55'}} />
        </span>
        <span style={{fontFamily: 'Cinzel, serif', fontWeight: 900, fontSize: '1.8rem', letterSpacing: '0.04em', color: '#9D4EDD', textShadow:'0 1px 8px #0008', flex: 1, minWidth: '100px'}}>VERLUME</span>
        <button onClick={()=>setShowDonate(true)} className="donate-btn" style={{background:accessibilityMode?'var(--border-color)':'none',border:accessibilityMode?'2px solid var(--text-primary)':'none',fontSize:accessibilityMode?'1.4rem':'1.8rem',color:accessibilityMode?'var(--bg-primary)':'#9D4EDD',cursor:'pointer',padding:accessibilityMode?'0.4rem':'0.2rem',minHeight:'44px',minWidth:'44px',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.2s',opacity:accessibilityMode?1:0.9,borderRadius:accessibilityMode?'6px':'0',filter:'saturate(1.2)'}} title="Donate" onMouseEnter={e=>{e.currentTarget.style.opacity='1'}} onMouseLeave={e=>{e.currentTarget.style.opacity=accessibilityMode?'1':'0.9'}}>�</button>
        <div style={{display:'none'}} />
        <select
          id="version-select"
          className="illuminated-input"
          value={selected}
          onChange={e => setSelected(e.target.value)}
          aria-label="Select Bible Version"
          style={{minWidth: 80, background:'var(--bg-tertiary)', color:'#9D4EDD', border:'1px solid #9D4EDD', borderRadius:6, padding:'0.3rem 0.6rem', fontSize:'0.9rem'}}
        >
          {versions.map((v: {name: string, file: string}) => (
            <option key={v.file} value={v.file}>{v.name}</option>
          ))}
        </select>
        <button style={{background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'1px solid #9D4EDD',borderRadius:6,padding:'0.3rem 0.6rem',cursor:'pointer',minWidth:'44px',minHeight:'44px',transition:'all 0.2s',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>onInfo && onInfo()} title="Info"><span className="material-icons">info</span></button>
      </nav>
            {/* Donate Modal/Page */}
            {showDonate && (
              <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}} onClick={()=>setShowDonate(false)}>
                <div style={{background:'var(--bg-secondary)',color:'var(--text-primary)',borderRadius:16,padding:'1.5rem',minWidth:'85%',maxWidth:420,boxShadow:'0 4px 32px #000a',position:'relative'}} onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>setShowDonate(false)} style={{position:'absolute',top:12,right:12,background:'none',border:'none',color:'#9D4EDD',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',minWidth:'44px',minHeight:'44px'}} aria-label="Close"><span className="material-icons">close</span></button>
                  <h2 style={{fontFamily:'Cinzel,serif',color:'#9D4EDD',fontWeight:900,marginBottom:'1.2rem',fontSize:'1.4rem',marginTop:0}}>Support Bible Explorer</h2>
                  <p style={{marginBottom:'1.2rem',fontSize:'1rem'}}>If you enjoy this app, consider supporting with a donation:</p>
                  <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
                    <a href="https://cash.app/$GlynnTheDragon" target="_blank" rel="noopener noreferrer" style={{background:'#00C244',color:'#fff',fontWeight:700,borderRadius:7,padding:'0.7rem 1rem',textDecoration:'none',fontSize:'1rem',display:'flex',alignItems:'center',gap:'0.6rem',minHeight:'44px'}}>
                      <span className="material-icons">account_balance_wallet</span> Donate with Cash App
                    </a>
                    <a href="https://venmo.com/GlynnTheDragon" target="_blank" rel="noopener noreferrer" style={{background:'#3D95CE',color:'#fff',fontWeight:700,borderRadius:7,padding:'0.7rem 1rem',textDecoration:'none',fontSize:'1rem',display:'flex',alignItems:'center',gap:'0.6rem',minHeight:'44px'}}>
                      <span className="material-icons">payment</span> Donate with Venmo
                    </a>
                  </div>
                  <p style={{marginTop:'1.5rem',fontSize:'0.95rem',color:'#9D4EDD',marginBottom:0}}>Thank you for your support!</p>
                </div>
              </div>
            )}


      {/* Navigation Sidebar (Drawer) */}
      {menuOpen && bibleStructure && (
        <>
          <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} />
          <aside className="sidebar-drawer" aria-label="Navigation Sidebar">
            <button
              className="illuminated-btn"
              aria-label="Close navigation menu"
              onClick={() => setMenuOpen(false)}
              style={{position:'absolute', top:18, right:18, fontSize:'1.5rem', padding: '0.3rem 1rem'}}
            >
              &#10005;
            </button>
            <div className="sidebar-title">Navigate</div>
            <label className="sidebar-label" htmlFor="sidebar-book">Book:</label>
            <select
              id="sidebar-book"
              className="sidebar-select"
              value={selectedBook}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setSelectedBook(e.target.value);
                if (bibleStructure) {
                  const book = bibleStructure.books.find((b: BibleBook) => b.name === e.target.value);
                  setSelectedChapter(book?.chapters[0]?.name || '');
                }
              }}
            >
              {bibleStructure?.books.map((book: BibleBook) => (
                <option key={book.name} value={book.name}>{book.name}</option>
              ))}
            </select>
            <label className="sidebar-label" htmlFor="sidebar-chapter">Chapter:</label>
            <select
              id="sidebar-chapter"
              className="sidebar-select"
              value={selectedChapter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedChapter(e.target.value)}
            >
              {(bibleStructure?.books.find((b: BibleBook) => b.name === selectedBook)?.chapters || []).map((chap: { name: string }) => (
                <option key={chap.name} value={chap.name}>{chap.name}</option>
              ))}
            </select>
            <button
              className="sidebar-gold-btn"
              onClick={() => {
                // Close the menu first
                setMenuOpen(false);
                
                // Find the selected chapter in the lines array
                const searchChapter = selectedChapter.split(' ').pop() || selectedChapter;
                const idx = lines.findIndex((line: string) => {
                  if (!line.startsWith('## ')) return false;
                  const lineChapter = line.replace('## ', '').trim();
                  // Try exact match first, then partial match
                  return lineChapter === selectedChapter || 
                         lineChapter === searchChapter ||
                         lineChapter.includes(selectedChapter) ||
                         selectedChapter.includes(lineChapter);
                });
                
                if (idx !== -1) {
                  setTimeout(() => {
                    // Try to find and scroll to the element
                    const el = document.querySelector(`[data-verse-idx="${idx}"]`);
                    if (el) {
                      (el as HTMLElement).scrollIntoView({behavior:'smooth', block:'start'});
                    }
                  }, 150);
                }
              }}
            >
              Go to Chapter
            </button>
          </aside>
        </>
      )}

      {/* Slide-out Menu - REMOVED, using FAB menu instead */}

      {/* Floating Action Button (FAB) Menu - Bottom Right */}
      <div style={{position:'fixed', bottom:'2rem', right:'2rem', zIndex:150, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'1rem'}}>
        {/* Menu items - show when FAB is open */}
        {fabMenuOpen && (
          <div style={{display:'flex', flexDirection:'column', gap:'0.8rem', background:'var(--bg-secondary)', borderRadius:'12px', padding:'1rem', boxShadow:'0 8px 24px rgba(0,0,0,0.3)', border:'2px solid var(--border-color)'}}>
            <button onClick={() => { handleBookmark(); setFabMenuOpen(false); }} style={{background:'none', border:'none', color:'var(--text-primary)', cursor:'pointer', fontSize:'1rem', fontWeight:600, textAlign:'left', padding:'0.6rem 1rem', borderRadius:'6px', transition:'all 0.2s'}} onMouseEnter={e => (e.currentTarget.style.background='var(--bg-tertiary)')} onMouseLeave={e => (e.currentTarget.style.background='none')}>Bookmarks</button>
            <button onClick={() => { handleExportImport(); setFabMenuOpen(false); }} style={{background:'none', border:'none', color:'var(--text-primary)', cursor:'pointer', fontSize:'1rem', fontWeight:600, textAlign:'left', padding:'0.6rem 1rem', borderRadius:'6px', transition:'all 0.2s'}} onMouseEnter={e => (e.currentTarget.style.background='var(--bg-tertiary)')} onMouseLeave={e => (e.currentTarget.style.background='none')}>Export/Import</button>
            <button onClick={() => { handleCompare(); setFabMenuOpen(false); }} style={{background:'none', border:'none', color:'var(--text-primary)', cursor:'pointer', fontSize:'1rem', fontWeight:600, textAlign:'left', padding:'0.6rem 1rem', borderRadius:'6px', transition:'all 0.2s'}} onMouseEnter={e => (e.currentTarget.style.background='var(--bg-tertiary)')} onMouseLeave={e => (e.currentTarget.style.background='none')}>Compare Versions</button>
            <button onClick={() => { getRandomScripture(); setFabMenuOpen(false); }} style={{background:'none', border:'none', color:'var(--text-primary)', cursor:'pointer', fontSize:'1rem', fontWeight:600, textAlign:'left', padding:'0.6rem 1rem', borderRadius:'6px', transition:'all 0.2s'}} onMouseEnter={e => (e.currentTarget.style.background='var(--bg-tertiary)')} onMouseLeave={e => (e.currentTarget.style.background='none')}>Random Scripture</button>
            <button onClick={() => { handleAccessibility(); setFabMenuOpen(false); }} style={{background:'none', border:'none', color:'var(--text-primary)', cursor:'pointer', fontSize:'1rem', fontWeight:600, textAlign:'left', padding:'0.6rem 1rem', borderRadius:'6px', transition:'all 0.2s'}} onMouseEnter={e => (e.currentTarget.style.background='var(--bg-tertiary)')} onMouseLeave={e => (e.currentTarget.style.background='none')}>Accessibility</button>
          </div>
        )}
        {/* FAB Button - Circle */}
        <button
          onClick={() => setFabMenuOpen(!fabMenuOpen)}
          style={{
            width:'60px',
            height:'60px',
            borderRadius:'50%',
            background:'var(--border-color)',
            border:'none',
            color:'var(--bg-primary)',
            fontSize:'1.8rem',
            cursor:'pointer',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            boxShadow:'0 4px 16px rgba(157,78,221,0.3)',
            transition:'all 0.3s',
            fontWeight:700
          }}
          onMouseEnter={e => {e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(157,78,221,0.5)'}}
          onMouseLeave={e => {e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(157,78,221,0.3)'}}
          aria-label="Menu"
          title="Menu"
        >
          ⋮
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-bar-container">
        <div id="search-form" className="search-bar" role="search">
          <input
            className="illuminated-input"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search verses..."
            aria-label="Search Bible"
            style={{flex:1, fontSize: '1rem', padding: '0.5rem 0.8rem'}}
          />
          <button className="search-btn" type="button" onClick={() => { setSearch(''); setShowAll(true); }} aria-label="Clear" title="Clear search" style={{display:'flex',alignItems:'center',justifyContent:'center'}}><span className="material-icons">close</span></button>
          <button className="voice-btn" type="button" onClick={listening ? stopListening : startListening} aria-label="Voice Search" title="Voice search" style={{display:'flex',alignItems:'center',justifyContent:'center',background:accessibilityMode?'var(--border-color)':'transparent',border:accessibilityMode?'2px solid var(--text-primary)':'none',color:accessibilityMode?'var(--bg-primary)':'var(--text-primary)',borderRadius:'6px',padding:accessibilityMode?'0.4rem':'0',minHeight:accessibilityMode?'44px':'auto',minWidth:accessibilityMode?'44px':'auto'}}>{accessibilityMode ? (listening ? '⏹️' : '🎤') : <span className="material-icons">{listening ? 'stop' : 'mic'}</span>}</button>
        </div>
      </div>
      {/* Show current voice transcript if listening */}
      {listening && (
        <div style={{margin:'0.8rem auto', color:'var(--text-tertiary)', fontStyle:'italic', fontSize:'1rem', background:'var(--bg-tertiary)', borderRadius:8, padding:'0.6rem 0.8rem', border:'1px solid var(--bg-tertiary)', minHeight:'2rem', display:'flex', alignItems:'center', gap:'0.6rem', maxWidth:'90%'}} aria-live="polite" aria-atomic="true">
          <span style={{fontSize:'1.2rem', color:'#8f6be8', animation: voiceTranscript ? 'pulse 1s infinite alternate' : undefined}} role="img" aria-label="Microphone">🎤</span>
          {voiceTranscript && <span style={{color:'var(--text-tertiary)', fontWeight:500, flexWrap:'wrap'}}>{voiceTranscript}</span>}
          {voiceError && <span style={{color:'#ff5252', fontWeight:600, marginLeft:8}} role="alert">{voiceError}</span>}
        </div>
      )}
      <style>{`
        @keyframes pulse {
          0% { filter: brightness(1); }
          100% { filter: brightness(1.5) drop-shadow(0 0 6px #8f6be8); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="bible-panel">
        {/* Render only the selected chapter for performance */}
        {(() => {
          if (!bibleStructure || lines.length === 0) return <div style={{padding:'2rem', textAlign:'center', color:'#999'}}>Loading...</div>;
          
          // Find the selected chapter line
          const chapterIdx = lines.findIndex(l => {
            if (!l.startsWith('## ')) return false;
            const lineChapter = l.replace('## ', '').trim();
            return lineChapter === selectedChapter;
          });
          
          if (chapterIdx === -1) {
            return <div style={{padding:'2rem', textAlign:'center', color:'#999'}}>Chapter not found</div>;
          }
          
          // Find the end of this chapter (next heading or end of file)
          let chapterEndIdx = chapterIdx + 1;
          while (chapterEndIdx < lines.length && !lines[chapterEndIdx].startsWith('#')) {
            chapterEndIdx++;
          }
          
          // Get chapter verses
          const chapterLines = lines.slice(chapterIdx, chapterEndIdx);
          const verseIndices: number[] = [];
          for (let i = 0; i < chapterLines.length; i++) {
            verseIndices.push(chapterIdx + i);
          }
          
          // Apply search filter if active
          const displayIndices = showAll ? verseIndices : searchResults.filter(i => verseIndices.includes(i));
          
          return displayIndices.map(i => {
            const line = lines[i];
            
            if (line.startsWith('## ')) {
              return <span key={i} className="bible-chapter" data-verse-idx={i} style={{
                marginTop:'1.5rem', display:'block', color:'var(--text-secondary)', fontWeight:600, fontSize:'1.2rem', fontFamily: 'Georgia, Times New Roman, Times, serif', borderBottom: '1px solid var(--text-secondary)', paddingBottom: '0.3rem',
                background: voiceHighlightIdx === i ? '#fff59d' : undefined,
                boxShadow: voiceHighlightIdx === i ? '0 0 0 3px #fff59d88' : undefined,
                transition: 'background 0.3s, box-shadow 0.3s',
              }}>{line.replace('## ', '')}</span>;
            }
            
            // Verse line
            const color = highlighted[i as number] || '';
            const isResult = !showAll && searchResults.includes(i as number);
            const isVoice = voiceHighlightIdx === i;
            return (
              <React.Fragment key={i}>
                <span
                  className="bible-verse"
                  style={{
                    display:'block',
                    margin:'0.3rem 0',
                    padding:'0.6rem 1rem',
                    borderRadius:'8px',
                    background: isVoice ? 'rgba(218,112,214,0.15)' : (isResult ? 'rgba(157,78,221,0.1)' : 'rgba(157,78,221,0.06)'),
                    outline: isResult ? '2px solid #9D4EDD' : undefined,
                    fontSize:'1.08rem',
                    minHeight:'2.4rem',
                    cursor:'pointer',
                    color: 'var(--text-primary)',
                    fontFamily: 'Georgia, Times New Roman, Times, serif',
                    lineHeight: 1.7,
                    boxShadow: isVoice ? '0 0 0 3px rgba(218,112,214,0.3), inset 0 0 0 1px #9D4EDD' : 'inset 0 0 0 1px rgba(157,78,221,0.2)',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    position: 'relative',
                  }}
                  onClick={() => handleHighlight(i as number)}
                  onContextMenu={e => { e.preventDefault(); handleAnnotate(i as number); }}
                  title="Click to highlight. Right-click to annotate."
                  tabIndex={0}
                  aria-label={`Verse: ${line}`}
                >
                  <span className="verse-name" style={{fontWeight:700, color:'#9D4EDD', fontFamily: 'Cinzel, serif', letterSpacing:'0.01em', marginRight:'0.3em'}}>{(line as string).split(' ')[0]}</span>
                  <span style={{color:'var(--text-primary)', fontWeight: color ? 600 : 400}} className={color ? `highlight-${color}` : ''}>{(line as string).substring((line as string).indexOf(' ')+1)}</span>
                </span>
                {annotateLine === i && (
                  <div>
                    <input
                      className="annotation-input"
                      type="text"
                      autoFocus
                      placeholder="Add annotation..."
                      defaultValue={annotations[i as number] || ''}
                      onBlur={e => saveAnnotation(i as number, e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveAnnotation(i as number, (e.target as HTMLInputElement).value);
                      }}
                      style={{fontFamily: 'Georgia, Times New Roman, Times, serif', fontSize: '1rem', borderRadius:8, border:'1.5px solid #9D4EDD', background:'var(--bg-secondary)', color:'var(--text-primary)', boxShadow:'0 0 0 3px rgba(157,78,221,0.1)', padding:'0.6rem', width:'100%', marginTop:'0.5rem'}}
                    />
                  </div>
                )}
                {annotations[i as number] && (
                  <div className="annotation" style={{fontFamily: 'Georgia, Times New Roman, Times, serif', color: '#DA70D6', background:'rgba(157,78,221,0.08)', borderRadius:6, marginTop:'0.5rem', padding:'0.6rem 0.8rem', border:'1px solid rgba(157,78,221,0.2)', display:'flex', gap:'0.5rem'}}>
                    <span className="material-icons" style={{fontSize:'1.2rem',color:'#9D4EDD',flexShrink:0}}>edit_note</span> <span>{annotations[i as number]}</span>
                  </div>
                )}
              </React.Fragment>
            );
          });
        })()}
        {/* Highlight confirmation dialog */}
        {highlightDialog.open && (
          <div className="highlight-dialog" style={{position:'fixed', top:0, left:0, width:'100vw', height:'100vh', background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem', backdropFilter:'blur(2.5px)'}}>
            <div style={{background:'rgba(35,35,43,0.98)', padding:'1.5rem', borderRadius:14, boxShadow:'0 4px 32px rgba(157,78,221,0.3)', minWidth:'85%', maxWidth:300, display:'flex', flexDirection:'column', alignItems:'center', animation:'fadeIn .3s ease'}}>
              <h3 style={{fontFamily:'Cinzel, serif', color:'#9D4EDD', fontWeight:900, fontSize:'1.2rem', marginBottom:'1rem', marginTop:0, letterSpacing:'0.01em'}}>Highlight?</h3>
              <div style={{display:'flex', gap:'0.8rem', margin:'1rem 0', flexWrap: 'wrap', justifyContent:'center'}}>
                {highlightColors.filter(c => c).map(c => (
                  <button key={c} style={{background:getHighlightColor(c), border:'2px solid #9D4EDD', borderRadius:8, width:40, height:40, cursor:'pointer', boxShadow:'0 2px 6px rgba(157,78,221,0.3)', transition:'transform 0.15s', outline:'none', minWidth:'44px', minHeight:'44px'}} onClick={() => confirmHighlight(c as HighlightColor)} aria-label={`Highlight ${c}`}></button>
                ))}
                <button onClick={() => confirmHighlight('')} style={{background:'var(--bg-secondary)', border:'2px solid #9D4EDD', borderRadius:8, width:52, height:52, cursor:'pointer', color:'#9D4EDD', boxShadow:'0 2px 6px rgba(157,78,221,0.3)', minWidth:'52px', minHeight:'52px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem', transition:'all 0.2s'}} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.1)'}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)'}} aria-label="Cancel highlight"><span className="material-icons" style={{fontSize:'32px'}}>close</span></button>
              </div>
              <button onClick={cancelHighlight} style={{marginTop:'1rem', background:'#9D4EDD', color:'var(--bg-primary)', border:'none', borderRadius:6, padding:'0.8rem 1.5rem', fontWeight:700, fontFamily:'Inter, sans-serif', fontSize:'1.05rem', boxShadow:'0 2px 6px rgba(157,78,221,0.3)', cursor:'pointer', minHeight:'52px', transition:'all 0.2s'}} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.05)'}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)'}}>Cancel</button>
            </div>
            <style>{`@keyframes fadeIn { from { opacity:0; transform:scale(0.9);} to {opacity:1; transform:scale(1);} }`}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default BibleExplorer;
